import { useEffect, useState, type FormEvent } from 'react';
import {
  reviewLogin,
  reviewLogout,
  reviewOrganizations,
  reviewAffiliations,
  reviewChangeAffiliation,
  reviewHistory,
  EntityReviewError,
  type ReviewOrganization,
  type ReviewAffiliation,
  type ReviewHistory,
} from '@urbanik/api-client';
import {
  CanonicalCard,
  CanonicalPageHeader,
  TextField,
  StatePanel,
  canonicalStates,
  formatDateTime,
} from '@urbanik/ui';
import './entity-foundation.css';
import { InstitutionalRegisterPanel } from './InstitutionalRegisterPanel';
import { InstitutionalConceptsPanel } from './InstitutionalConceptsPanel';
import { reviewApiBase } from './api-base';
import { EntityHouseholdReviewPanel } from './EntityHouseholdReviewPanel';
import { EntityQuestionnaireReviewPanel } from './EntityQuestionnaireReviewPanel';
import { EntityImportReviewPanel } from './EntityImportReviewPanel';
import { EntityStatusAuthorizationReviewPanel } from './EntityStatusAuthorizationReviewPanel';
// Same renderer for Web/PWA/Electron; no persistent context or financial routes.
const base = () => reviewApiBase;
function Failure({ error, retry }: { error: unknown; retry: () => void }) {
  const kind = error instanceof EntityReviewError && error.status === 403 ? 'forbidden' : 'error';
  return <StatePanel state={canonicalStates.find((s) => s.kind === kind)!} onAction={retry} />;
}
export function AffiliationPanel({ organization }: { organization: ReviewOrganization }) {
  const [rows, setRows] = useState<readonly ReviewAffiliation[]>([]);
  const [history, setHistory] = useState<readonly ReviewHistory[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selected, setSelected] = useState<ReviewAffiliation | null>(null);
  const [reason, setReason] = useState('');
  useEffect(() => {
    const abort = new AbortController();
    let live = true;
    setRows([]);
    setHistory(null);
    setError(null);
    setLoading(true);
    setSelected(null);
    void reviewAffiliations(base(), organization.id, abort.signal)
      .then((r) => {
        if (live) setRows(r.items);
      })
      .catch((e) => {
        if (live) setError(e);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      abort.abort();
    };
  }, [organization.id, reload]);
  useEffect(() => {
    if (!selected) return;
    const abort = new AbortController();
    let live = true;
    setHistory(null);
    void reviewHistory(base(), selected, abort.signal)
      .then((r) => {
        if (live) setHistory(r);
      })
      .catch((e) => {
        if (live) setError(e);
      });
    return () => {
      live = false;
      abort.abort();
    };
  }, [selected]);
  async function change(event: FormEvent) {
    event.preventDefault();
    if (!selected || saving) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await reviewChangeAffiliation(base(), selected, reason, crypto.randomUUID());
      setReason('');
      setSuccess(true);
      setReload((v) => v + 1);
    } catch (e) {
      setRows([]);
      setHistory(null);
      setError(e);
    } finally {
      setSaving(false);
    }
  }
  return (
    <section aria-label={'Afiliaciones de ' + organization.code}>
      <h2>{organization.name}</h2>
      <p>
        {organization.kind === 'cooperative' ? 'Cooperativa' : 'Comité'} · Operación deshabilitada ·
        Sin declaración de estado jurídico
      </p>
      <small>Entidad: {organization.id}</small>
      {success ? (
        <p role="status">Cambio guardado con historial. No modifica otras afiliaciones.</p>
      ) : null}
      {loading ? (
        <StatePanel state={canonicalStates[0]!} />
      ) : error ? (
        <Failure error={error} retry={() => setReload((v) => v + 1)} />
      ) : !rows.length ? (
        <StatePanel state={canonicalStates.find((s) => s.kind === 'empty')!} />
      ) : (
        <>
          {rows.map((row) => (
            <CanonicalCard className="entity-review-card" key={row.id}>
              <h3>{row.memberName}</h3>
              <p>
                Afiliación {row.status === 'active' ? 'activa' : 'inactiva'} · Versión {row.version}
              </p>
              <dl>
                <dt>Identidad compartida</dt>
                <dd>{row.memberId}</dd>
                <dt>Afiliación independiente</dt>
                <dd>{row.id}</dd>
                <dt>Fecha de ingreso</dt>
                <dd>{row.joinedOn ?? 'Desconocida'}</dd>
                <dt>Fecha de fin</dt>
                <dd>{row.endedOn ?? 'Desconocida'}</dd>
              </dl>
              <button
                className="ui-button ui-button--secondary"
                disabled={saving}
                onClick={() => setSelected(row)}
              >
                Ver historial y acciones
              </button>
            </CanonicalCard>
          ))}
          {selected ? (
            <CanonicalCard className="entity-review-card">
              <h3>Historial protegido</h3>
              {history === null ? (
                <p role="status">Cargando historial…</p>
              ) : (
                <ol>
                  {history.map((entry, i) => (
                    <li key={i}>
                      {entry.action === 'created' ? 'Afiliación creada' : 'Estado cambiado'} ·{' '}
                      {formatDateTime(entry.at)}
                      <br />
                      Actor: {entry.actorId}
                      <br />
                      {entry.reason ?? 'Alta de prueba; fechas desconocidas'}
                    </li>
                  ))}
                </ol>
              )}
              {organization.permissions.includes('affiliations.manage') ? (
                <form onSubmit={(e) => void change(e)}>
                  <TextField
                    id="affiliation-reason"
                    label="Motivo del cambio (mínimo 5 caracteres)"
                    value={reason}
                    required
                    minLength={5}
                    maxLength={500}
                    disabled={saving}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <button
                    className="ui-button ui-button--primary"
                    disabled={saving || reason.trim().length < 5}
                  >
                    {saving
                      ? 'Guardando…'
                      : selected.status === 'active'
                        ? 'Confirmar inactivación de prueba'
                        : 'Confirmar activación de prueba'}
                  </button>
                </form>
              ) : (
                <p>Tu acceso permite consulta, no cambios.</p>
              )}
            </CanonicalCard>
          ) : null}
        </>
      )}
    </section>
  );
}
export function EntityFoundationView({
  concepts = false,
  register = false,
}: {
  concepts?: boolean;
  register?: boolean;
}) {
  const routeName = register
    ? 'fundacion-registro'
    : concepts
      ? 'fundacion-conceptos'
      : 'fundacion-entidades';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizations, setOrganizations] = useState<readonly ReviewOrganization[] | null>(null);
  const [organizationId, setOrganizationId] = useState(
    () => new URLSearchParams(window.location.hash.split('?')[1]).get('entity') ?? '',
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    let live = true;
    setOrganizations(null);
    setError(null);
    setLoading(true);
    void reviewOrganizations(base(), abort.signal)
      .then((r) => {
        if (live) setOrganizations(r.items);
      })
      .catch((e) => {
        if (live && (!(e instanceof EntityReviewError) || e.status !== 401)) setError(e);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
      abort.abort();
    };
  }, [refresh]);
  useEffect(() => {
    let pending: AbortController | null = null;
    const focus = () => {
      if (loading) return;
      pending?.abort();
      const controller = new AbortController();
      pending = controller;
      // Revalidate access without unmounting active forms after the native file picker.
      void reviewOrganizations(base(), controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) {
            setOrganizations(result.items);
            setError(null);
          }
        })
        .catch((failure: unknown) => {
          if (!controller.signal.aborted) {
            setOrganizations(null);
            setError(
              failure instanceof EntityReviewError && failure.status === 401 ? null : failure,
            );
          }
        });
    };
    const hash = () =>
      setOrganizationId(
        new URLSearchParams(window.location.hash.split('?')[1]).get('entity') ?? '',
      );
    window.addEventListener('focus', focus);
    window.addEventListener('hashchange', hash);
    return () => {
      pending?.abort();
      window.removeEventListener('focus', focus);
      window.removeEventListener('hashchange', hash);
    };
  }, [refresh, loading]);
  async function login(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setOrganizations(null);
    setOrganizationId('');
    try {
      await reviewLogin(base(), email, password);
      setPassword('');
      window.location.hash = routeName;
      setRefresh((v) => v + 1);
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  }
  async function logout() {
    setOrganizations(null);
    setOrganizationId('');
    setLoading(true);
    try {
      await reviewLogout(base());
      setRefresh((v) => v + 1);
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  }
  const selected = organizations?.find((org) => org.id === organizationId);
  const contextKey = selected
    ? JSON.stringify([refresh, selected.id, [...selected.permissions].sort()])
    : '';
  return (
    <main className="entity-review">
      <CanonicalPageHeader
        title={
          register
            ? 'Actas y reglas por entidad'
            : concepts
              ? 'Conceptos por entidad'
              : 'Fundación multi-entidad'
        }
        description={
          (concepts || register ? 'CR-MULTI-001.2 · PUERTA A' : 'CR-MULTI-001.1') +
          ' · DATOS FICTICIOS · Entorno aislado'
        }
      />
      <p className="entity-review-banner">
        Solo revisión administrativa. COMIVALLE no se modifica. No hay pagos, saldos ni entidades
        reales habilitadas.
      </p>
      {loading ? (
        <StatePanel state={canonicalStates[0]!} />
      ) : (
        <>
          {error ? <Failure error={error} retry={() => setRefresh((v) => v + 1)} /> : null}
          {organizations === null ? (
            <form className="ui-canonical-card entity-review-card" onSubmit={(e) => void login(e)}>
              <h2>Acceso de prueba independiente</h2>
              <TextField
                id="review-email"
                label="Correo de prueba"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                id="review-password"
                label="Contraseña de prueba"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button className="ui-button ui-button--primary">Iniciar revisión</button>
            </form>
          ) : (
            <>
              <button className="ui-button ui-button--secondary" onClick={() => void logout()}>
                Cerrar sesión de prueba
              </button>
              {!organizations.length ? (
                <StatePanel
                  state={{
                    ...canonicalStates.find((s) => s.kind === 'empty')!,
                    title: 'Sin entidades concedidas',
                    message:
                      'La cuenta existe, pero no tiene acceso institucional. Ningún rol global concede acceso automáticamente.',
                  }}
                />
              ) : (
                <label className="entity-review-selector">
                  Entidad de revisión
                  <select
                    value={organizationId}
                    onChange={(e) => {
                      setOrganizationId(e.target.value);
                      window.location.hash =
                        routeName +
                        (e.target.value ? '?entity=' + encodeURIComponent(e.target.value) : '');
                    }}
                  >
                    <option value="">Selecciona una entidad</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {selected ? (
                register ? (
                  <InstitutionalRegisterPanel key={contextKey} organization={selected} />
                ) : concepts ? (
                  <InstitutionalConceptsPanel key={contextKey} organization={selected} />
                ) : (
                  <div key={contextKey}>
                    <AffiliationPanel organization={selected} />
                    <EntityHouseholdReviewPanel organization={selected} />
                    <EntityQuestionnaireReviewPanel organization={selected} />
                    <EntityImportReviewPanel organization={selected} />
                    <EntityStatusAuthorizationReviewPanel organization={selected} />
                  </div>
                )
              ) : organizationId ? (
                <StatePanel state={canonicalStates.find((s) => s.kind === 'forbidden')!} />
              ) : null}
            </>
          )}
        </>
      )}
    </main>
  );
}
